<script setup lang="ts">
import { isFilled } from '@prismicio/client'
import type { Content } from '@prismicio/client'

const props = defineProps(getSliceComponentProps<Content.AircoPackagesGridSlice>())

const { client } = usePrismic()
const { data: packages } = await useAsyncData(
  `airco-packages-${props.slice.id}`,
  () => client.getAllByType('airco_package', {
    orderings: [{ field: 'my.airco_package.from_price' }],
  }),
)

const priceFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const formatPrice = (value: number | null | undefined) =>
  typeof value === 'number' ? priceFormatter.format(value) : ''

const popularLabel = computed(() => props.slice.primary.popular_label || 'Most popular')
const pricePrefix = computed(() => props.slice.primary.price_prefix || 'vanaf')
const priceSuffix = computed(() => props.slice.primary.price_suffix || '')

const hasEnterprise = computed(() =>
  isFilled.richText(props.slice.primary.enterprise_title)
  || isFilled.richText(props.slice.primary.enterprise_description)
  || isFilled.link(props.slice.primary.enterprise_cta_link),
)

const gridWidthClass = computed(() => {
  const count = packages.value?.length ?? 0
  if (count === 1) return 'lg:w-1/3'
  if (count === 2) return 'lg:w-2/3'
  return ''
})

const gridColsClass = computed(() => {
  const count = packages.value?.length ?? 0
  if (count === 1) return 'lg:grid-cols-1'
  if (count === 2) return 'lg:grid-cols-2'
  if (count === 3) return 'lg:grid-cols-3'
  if (count >= 4) return 'lg:grid-cols-2 xl:grid-cols-4'
  return ''
})
</script>

<template>
  <section
      :data-slice-type="slice.slice_type"
      :data-slice-variation="slice.variation"
      class="pa-packages"
  >
    <div class="pa-container">
      <div v-if="slice.primary.eyebrow || isFilled.richText(slice.primary.heading)" class="pa-packages__header">
        <div v-if="slice.primary.eyebrow" class="pa-packages__eyebrow">
          {{ slice.primary.eyebrow }}
        </div>
        <div v-if="isFilled.richText(slice.primary.heading)" class="pa-packages__heading">
          <PrismicRichText :field="slice.primary.heading" />
        </div>
      </div>

      <div class="pa-packages__inner">
        <div
            :class="[
              'pa-packages__grid',
              `pa-packages__grid--count-${packages?.length || 0}`,
              gridColsClass,
              gridWidthClass,
            ]"
        >
          <article
              v-for="pkg in packages"
              :key="pkg.id"
              :class="['pa-packages__card', { 'pa-packages__card--popular': pkg.data.is_popular }]"
          >
          <div v-if="pkg.data.image?.url || pkg.data.is_popular" class="pa-packages__image-wrap">
            <div v-if="pkg.data.image?.url" class="pa-packages__image">
              <PrismicImage
                  :field="pkg.data.image"
                  :widths="[400, 600, 800, 1200]"
                  sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
                  :imgix-params="{ ar: '4:3', fit: 'crop', auto: 'format,compress' }"
              />
            </div>
            <div v-if="pkg.data.is_popular" class="pa-packages__popular">
              <span>{{ popularLabel }}</span>
              <span aria-hidden="true">&#9733;</span>
            </div>
          </div>

          <div class="pa-packages__body">
            <h3 class="pa-packages__title">{{ pkg.data.title }}</h3>
            <p v-if="pkg.data.subtitle" class="pa-packages__subtitle">
              {{ pkg.data.subtitle }}
            </p>

            <div v-if="typeof pkg.data.from_price === 'number'" class="pa-packages__price">
              <span class="pa-packages__price-amount">{{ formatPrice(pkg.data.from_price) }}</span>
              <span class="pa-packages__price-meta">
                <span v-if="pricePrefix">{{ pricePrefix }}</span>
                <span v-if="priceSuffix">{{ priceSuffix }}</span>
              </span>
            </div>

            <ul v-if="pkg.data.usps?.length" class="pa-packages__usps">
              <li
                  v-for="(row, index) in pkg.data.usps"
                  :key="index"
                  class="pa-packages__usp"
              >
                <span class="pa-packages__usp-check" aria-hidden="true">&#10003;</span>
                <span>{{ row.usp }}</span>
              </li>
            </ul>

            <TrackedPrismicLink
                v-if="isFilled.link(pkg.data.cta_link)"
                :field="pkg.data.cta_link"
                :track="pkg.data.cta_track_event"
                class="pa-btn pa-btn--primary pa-packages__cta"
            >
              {{ pkg.data.cta_link.text || 'Get started' }}
            </TrackedPrismicLink>
          </div>
          </article>
        </div>

        <div v-if="hasEnterprise" class="pa-packages__enterprise">
        <div class="pa-packages__enterprise-content">
          <div v-if="isFilled.richText(slice.primary.enterprise_title)" class="pa-packages__enterprise-title">
            <PrismicRichText :field="slice.primary.enterprise_title" />
          </div>
          <div v-if="isFilled.richText(slice.primary.enterprise_description)" class="pa-packages__enterprise-description">
            <PrismicRichText :field="slice.primary.enterprise_description" />
          </div>
        </div>
        <TrackedPrismicLink
            v-if="isFilled.link(slice.primary.enterprise_cta_link)"
            :field="slice.primary.enterprise_cta_link"
            :track="slice.primary.enterprise_track_event"
            class="pa-btn pa-btn--primary pa-packages__enterprise-cta"
        >
          {{ slice.primary.enterprise_cta_link.text || 'Get in touch' }}
        </TrackedPrismicLink>
        </div>
      </div>
    </div>
  </section>
</template>
